<template>
    <ns-modal
        v-model="dialog"
        :fullscreen="fullScreen"
        title=''
        @input="closeDialog()"
        data-test-id="editUsernameDialog"
    >
        <loader v-show="isLoading" />
        <server-error v-if="!!dialogError" :error="dialogError" />
        <v-form ref="editUsernameForm" v-model="emailAddressForm" @submit.prevent >
            <section class="current-email-details">
                <div class="editEmail" data-test-id="editEmailModalHeader">
                    {{sharedStrings.email_verification.edit_email_modal_header}}
                </div>
                <div class="emailAddress" data-test-id="emailCardCurrentEmail">
                    {{sharedStrings.email_verification.email_verify_card_current_email}}: {{email}}
                </div>
                <div class="emailAddress" data-test-id="emailCardCurrentUsername">
                    {{sharedStrings.email_verification.email_verify_card_current_username}}: {{getUsername}}
                </div>
            </section>
            <section>
                <NsEmailSimple
                    v-model="emailAddress"
                    :label="sharedStrings.email_verification.edit_email_modal_new_email_label"
                    required
                    :duped-email-text="sharedStrings.forms.fieldBadEmail"
                    :required-error-message="sharedStrings.forms.fieldRequired"
                    :invalid-email-text="sharedStrings.forms.fieldBadEmail"
                    data-testid="email_address"
                    maxlength
                    autofocus
                    :check-email="checkEmail"
                    @input="emailInput"
                    :placeholder="sharedStrings.email_verification.email_input_username_warning"
                />
            </section>
        </v-form>
        <template v-slot:actions>
            <v-layout column>
                <v-flex xs12>
                    <button
                        flat
                        block
                        class="primary-hit mb-2 fluid saveButton"
                        :disabled="buttonDisabled"
                        @click="editEmail()"
                    >{{sharedStrings.generic.save}}</button>
                </v-flex>
                <div class="confirmation">
                    {{ sharedStrings.email_verification.new_email_confirmation_sent }}
                </div>
                <div class="info-text"> 
                    <div class="info-text-left">
                        <NsIcon
                            icon-name="icon-info-filled"
                            color="#252525"
                            style="width: 24px; height: 24px"
                        />
                    </div>
                    <div class="info-text-right">
                        {{ sharedStrings.email_verification.email_verification_email_linked_account }}
                    </div>
                </div>
            </v-layout>
        </template>
    </ns-modal>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { UserService } from '@nuskin/ns-account'
import { NsIcon } from '@nuskin/ns-icon';
import { NsModal, NsEmailSimple } from '@nuskin/design-components';
import ServerError from '../ServerError';
import profileUtils from '../../repositories/profileUtils';
import { UsesFeatureFlags } from '@nuskin/ns-feature-flags-vue'
import Loader from '../Loader.vue'

export default {
    components: {
        NsModal,
        NsEmailSimple,
        ServerError,
        Loader,
        NsIcon
    },
    mixins: [UsesFeatureFlags],
    data() {
        return {
            dialog: true,
            dialogError: null,
            emailAddress: '',
            emailAddressForm: {},
            buttonDisabled: true,
            isLoading: false,
            // eslint-disable-next-line security/detect-unsafe-regex, max-len
            emailPattern: /^[\s]*[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?[\s]*$/
        };
    },
    computed: {
        ...mapGetters({
            txc: 'translationsPersonal',
            sharedStrings: 'sharedStrings',
            fullScreen: 'mobileView',
            account: 'account',
            getUsername: 'getUsername',
            email: 'email'
        }),
        email() {
            return UserService.getUser().email
        }
    },
    methods: {
        ...mapActions([
            'setAccount',
            'updateEmail',
            'setModified',
            'setUsername',
            'setEmailVerified',
            'setEmailCardShowing',
            'setPendingStatus',
            'setPendingUsername',
            'setErrorMsg',
            'setEmailSentHavingIssue',
            'setResendVerifyEmail'
        ]),
        closeDialog() {
            this.$emit('closeDialog');
            this.dialogError = null;
        },
        emailInput(inputValue) {
            if (!this.emailPattern.test(inputValue)) {
                this.buttonDisabled = true
            }
        },
        async checkEmail() {
            const [
                isFormatValid,
                isDomainValid,
                isAvailable
            ] = await profileUtils.isDuplicateEmail(
                encodeURIComponent(this.emailAddress),
            );
            const isServiceValid = Boolean(isFormatValid && isDomainValid);

            if (isServiceValid && isAvailable) {
                this.buttonDisabled = false
            } else {
                this.buttonDisabled = true
            }
            
            return [isServiceValid, isAvailable];
        },
        setEmailSentPending(newEmail)
        {
            this.setEmailCardShowing(true)
            localStorage.removeItem('successNotificationConfirmed')
            this.setEmailVerified(false)
            this.setPendingUsername(newEmail)
            this.setPendingStatus(true)
            this.setModified(true)
            this.setResendVerifyEmail(false)
        },
        async setEmailVerifyStatus(newEmail) {
            const verifyStatus = await profileUtils.verifyStatus();
            if (verifyStatus && !verifyStatus.verified) {
                this.setEmailSentPending(newEmail)
            }
            this.setErrorMsg(this.sharedStrings.email_verification.email_verify_card_error_content)
            this.setEmailSentHavingIssue(true)
            this.isLoading = false;
            this.closeDialog();
        },
        async editEmail() {
            this.isLoading = true;
            this.setEmailSentHavingIssue(false)
            const updateResult = await profileUtils.updateEmail(this.emailAddress)
            if(updateResult.status === 200) {
                this.setEmailSentPending(this.emailAddress)
                this.isLoading = false;
                this.closeDialog();
            } else {
                this.setEmailVerifyStatus(this.emailAddress)
            }
            profileUtils.publishAnalytics(
                this.$analytics.category.personal,
                'personal_email_address',
                'updated', 
            );
        }
    },
    created() {
        profileUtils.publishAnalytics(
            this.$analytics.category.personal,
            'personal_email_address_dialog',
            'opened'
        );
    },
    destroyed() {
        profileUtils.publishAnalytics(
            this.$analytics.category.personal,
            'personal_email_address_dialog',
            'closed'
        );
    }
};
</script>

<style scoped lang="scss">
@use "sass:map";
@use '~@nuskin/ns-core-styles/src/scss/global.mixins';
.saveButton {
    @media screen and (min-width: 769px) {
        margin-top: -24px;
	}
    @media screen and (max-width: 768px) {
		margin-top: 0;
	}
}

.modal-desc {
    @include global.p-small();
    margin-bottom: 16px;
}
.ns-email {
    position: relative;
    font-weight: 400;
    line-height: 22px;
}
.confirmation {
    font-size: 14px;
    line-height: 20px;
    font-weight: 400;
    margin-top: 5px;
    margin-bottom: 15px;
}
.current-email {
    &-details {
        margin-top: 25px;
        padding: 0;
        margin-bottom: 25px;
        .editEmail{
            margin-bottom: 10px;
            font-weight: bold;
            font-size: 18px;
            line-height: 1.5rem;
        }
        .emailAddress {
            margin-bottom: 5px;
            font-size: 16px;
        }
    }
    &-content {
        padding: 0;
        margin-bottom: 25px;
    }
}
.info-text {
    display: flex;
    justify-content: flex-start;
    margin-top: 10px;
    background: #f6f7f7;
    padding: 8px;
    align-items: center;
    &-left {
        text-align: center;
    }
    &-right {
        font-size: 16px;
        font-weight: 600;
        line-height: 21px;
        color: #272525;
        margin-left: 8px;
    }
}
</style>
